import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import api from '../../utils/api';

interface VehicleDeletionInfo {
  vehicle: {
    id: number;
    plate_number: string;
    model: string;
    conductor_name: string;
    brand_name: string;
  };
  can_delete: boolean;
  active_orders_count: number;
  completed_orders_count: number;
  total_orders_count: number;
  warning_message: string;
}

interface DeleteVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
  companyId: string;
  vehicleId: number;
  vehiclePlateNumber: string;
}

const DeleteVehicleModal: React.FC<DeleteVehicleModalProps> = ({
  isOpen,
  onClose,
  onDelete,
  companyId,
  vehicleId,
  vehiclePlateNumber
}) => {
  const [deletionInfo, setDeletionInfo] = useState<VehicleDeletionInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && vehicleId) {
      fetchDeletionInfo();
    }
  }, [isOpen, vehicleId, companyId]);

  const fetchDeletionInfo = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/companies/${companyId}/vehicles/${vehicleId}/deletion-info`);
      setDeletionInfo(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des infos de suppression :', error);
      setError('Échec du chargement des informations du véhicule');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletionInfo?.can_delete) return;

    if (confirmText !== deletionInfo.vehicle.plate_number) {
      setError('Veuillez saisir exactement le numéro d\'immatriculation pour confirmer la suppression');
      return;
    }

    setDeleting(true);
    setError('');

    try {
      await api.delete(`/companies/${companyId}/vehicles/${vehicleId}`);
      await onDelete();
      handleClose();
    } catch (error) {
      console.error('Erreur lors de la suppression du véhicule :', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Échec de la suppression du véhicule. Veuillez réessayer.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    setDeletionInfo(null);
    setConfirmText('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Supprimer le véhicule</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-2 text-gray-600">Chargement des informations du véhicule...</span>
            </div>
          ) : deletionInfo ? (
            <div className="space-y-4">
              {/* Infos véhicule */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">Détails du véhicule</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><span className="font-medium">Immatriculation :</span> {deletionInfo.vehicle.plate_number}</p>
                  <p><span className="font-medium">Modèle :</span> {deletionInfo.vehicle.brand_name} {deletionInfo.vehicle.model}</p>
                  <p><span className="font-medium">Conducteur :</span> {deletionInfo.vehicle.conductor_name}</p>
                </div>
              </div>

              {/* Infos ordres de service */}
              {deletionInfo.total_orders_count > 0 && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">Historique de service</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>Ordres de service totaux : {deletionInfo.total_orders_count}</p>
                    <p>Ordres complétés : {deletionInfo.completed_orders_count}</p>
                    {deletionInfo.active_orders_count > 0 && (
                      <p className="text-red-600 font-medium">
                        Ordres actifs : {deletionInfo.active_orders_count}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Message d'avertissement */}
              <div className={`rounded-lg p-4 ${
                deletionInfo.can_delete 
                  ? 'bg-orange-50 border border-orange-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <p className={`text-sm ${
                  deletionInfo.can_delete ? 'text-orange-800' : 'text-red-800'
                }`}>
                  {deletionInfo.warning_message}
                </p>
              </div>

              {/* Champ de confirmation */}
              {deletionInfo.can_delete && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Tapez le numéro d'immatriculation "{deletionInfo.vehicle.plate_number}" pour confirmer la suppression :
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={deletionInfo.vehicle.plate_number}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              )}

              {/* Message d'erreur */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Échec du chargement des informations du véhicule</p>
            </div>
          )}
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            disabled={deleting}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Annuler
          </button>

          {deletionInfo?.can_delete && (
            <button
              onClick={handleDelete}
              disabled={deleting || confirmText !== deletionInfo.vehicle.plate_number}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {deleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 size={16} />
                  Supprimer le véhicule
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteVehicleModal;
